import React, { useCallback, useEffect, useRef, useState } from 'react'
import { HereMapClickEvent } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { useData } from '@user/features/MapView/MapBubbleContents/hooks'
import { useMapLibreTooltipPosition } from '@user/components/HereMap/MapPin/components/MapPinTooltip/hooks/useMapLibreTooltipPosition'
import { StatusSection } from '@user/features/MapView/MapBubbleContents/atoms/StatusSection'
import { ViewKnockStatusHistoryButton } from '@user/features/MapView/MapBubbleContents/atoms/ViewKnockStatusHistoryButton'
import { formatAddress } from '@user/features/MapView/utils/formatAddress'
import { useRecordsHiddenByFilters } from '@user/features/MapView/RecordsHiddenByFilters/useRecordsHiddenByFilters'
import { HIDDEN_RECORDS_WARNING } from '@user/features/MapView/RecordsHiddenByFilters/recordsHiddenByFilters'
import { UpdateFiltersButton } from '@user/features/MapView/RecordsHiddenByFilters/UpdateFiltersButton'
import MapBubbleContentsAlert from '@user/features/MapView/MapBubbleContents/MapBubbleContentsAlert'
import maplibregl from 'maplibre-gl'
import {
  ClearKnockStatusButton,
  ClearKnockStatusConfirmDialog,
  applyClearedOverride,
  useClearKnockStatus,
  useKnockStatusEditPermission,
} from '@user/features/MapView/ClearKnockStatus'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateKnockStatusHistoryById } from '@user/store/knockStatus/api'
import { toast } from '@sales-center/components'

type Props = {
  map: maplibregl.Map
  data: HereMapClickEvent | null
  onAddLead: () => void
  onClose: () => void
  onMouseEnter: () => void
  onMouseLeave?: () => void
  onKnockStatusClick?: (entityId: string, variant: string) => void
  onViewKnockStatusHistory?: () => void
  /** Re-fetches the map after the active knock status is cleared (SH-661). */
  onKnockStatusCleared?: () => void
  showKnockStatusButton?: boolean
  pendingCustomReason: { pinId: number; reason: string | null } | null
  // SH-1817 — push an edited reason into the pending cache so the open bubble
  // reflects it immediately after save, before the tile refetch re-resolves the pin.
  setPendingCustomReason?: (value: {
    pinId: number
    reason: string | null
  }) => void
  // SH-1143 — only run the hidden-records lookup for a committed empty-rooftop click,
  // never for hover previews (avoids a fetch + banner recompute on every hover).
  checkHiddenRecords?: boolean
}

const TOOLTIP_WIDTH = 340

function arrowAnchorStyle(position: string, arrowOffset: number) {
  if (position === 'right')
    return { left: '-11px', top: `calc(50% + ${arrowOffset}px - 30px)` }
  if (position === 'left')
    return { right: '-11px', top: `calc(50% + ${arrowOffset}px - 30px)` }
  if (position === 'bottom')
    return { top: '-8px', left: `calc(50% + ${arrowOffset}px - 15px)` }
  return { bottom: '-14px', left: `calc(50% + ${arrowOffset}px - 15px)` }
}

export function MapLibreAddLeadMapPinTooltip({
  map,
  data,
  onAddLead,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onKnockStatusClick,
  onViewKnockStatusHistory,
  onKnockStatusCleared,
  showKnockStatusButton,
  pendingCustomReason,
  setPendingCustomReason,
  checkHiddenRecords = false,
}: Props) {
  const isD2dEnabled = useD2DKnockEnabled()
  const { variant, knockStatusPreview, knockStatusLocationId } = useData({
    data,
    pendingCustomReason,
  })

  const [canEdit] = useKnockStatusEditPermission('KnockStatus')
  const hasKnockStatus =
    knockStatusPreview.hasKnockStatusLocation &&
    !!knockStatusPreview.status &&
    knockStatusLocationId !== null

  // Standalone knock-status-only pin: backend resolves 'Unknown' -> 'KnockStatus' (SH-851).
  const {
    clearConfirmOpen,
    isClearing,
    cleared,
    openClearConfirm,
    closeClearConfirm,
    handleClearKnockStatus,
  } = useClearKnockStatus({
    knockStatusLocationId,
    variant: variant ?? '',
    entityType: 'Unknown',
    onRefresh: onKnockStatusCleared,
  })

  const dispatch = useAppDispatch()
  const [isSavingReason, setIsSavingReason] = useState(false)

  // SH-1817 -- edit the active knock status reason from the standalone
  // knock-status-only pin's preview bubble. Same shape as the PestPac bubble:
  // these pins have no backing Lead/Opp/DraftLead, so EntityType is always
  // 'KnockStatus' (the strict-enum member for a record-less knock status).
  const handleSaveReason = useCallback(
    async (newReason: string | null) => {
      const id = knockStatusPreview.id
      if (!id) return false
      setIsSavingReason(true)
      try {
        const entityType = 'KnockStatus'
        await dispatch(
          updateKnockStatusHistoryById({
            id,
            entityType,
            input: {
              customReason: newReason,
              knockStatusName: null,
              previousKnockStatusName: null,
              entityType,
              entityId: 0,
            },
          }),
        ).unwrap()
        // SH-1817 -- reflect the new reason in the open bubble immediately via
        // the pending-reason cache (keyed by this pin's knockStatusLocationId),
        // before the tile refetch re-resolves the pin.
        if (knockStatusLocationId !== null) {
          setPendingCustomReason?.({
            pinId: knockStatusLocationId,
            reason: newReason,
          })
        }
        toast.success('Reason updated')
        return true
      } catch (e: any) {
        toast.error(e?.message || 'Failed to update reason')
        return false
      } finally {
        setIsSavingReason(false)
      }
    },
    [
      knockStatusPreview.id,
      knockStatusLocationId,
      setPendingCustomReason,
      dispatch,
    ],
  )
  const { hiddenExists, applyFilterUpdate } = useRecordsHiddenByFilters(
    data?.recordsAtLocation,
    !!data && checkHiddenRecords,
  )
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [tooltipHeight, setTooltipHeight] = useState(180)

  useEffect(() => {
    const el = tooltipRef.current
    if (!el) return
    const measure = () => {
      const height = el.offsetHeight
      if (height > 0) setTooltipHeight(height)
    }
    measure()
    // SH-1817 -- the reason field can expand into an inline editor (pencil),
    // changing the tooltip height. Observe resize so the position/arrow math
    // stays correct instead of using the last statically-sampled height.
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [data, isD2dEnabled, showKnockStatusButton, variant, hiddenExists])

  // SH-1143 — reveal the hidden record(s) by removing only the relevant filters, then close the
  // warning bubble. Pins refetch and filter chips / side-sheet update automatically off the saved prefs.
  const handleUpdateFilters = () => {
    applyFilterUpdate()
    onClose()
  }

  const { position, arrowOffset, left, top, transform, isOutOfViewport } =
    useMapLibreTooltipPosition({
      map,
      latitude: data?.latitude || 0,
      longitude: data?.longitude || 0,
      tooltipWidth: TOOLTIP_WIDTH,
      tooltipHeight,
      verticalTop: !knockStatusPreview.hasKnockStatusLocation ? 10 : 41,
    })

  if (!data || isOutOfViewport) return null
  const { address, latitude, longitude, label } = data

  const addressDisplay = formatAddress(
    address?.street1 || '',
    address?.street2 || '',
    address?.city || '',
    address?.stateAbbreviation || '',
    address?.postalCode || '',
  )
  return (
    <div
      ref={tooltipRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'absolute',
        left,
        top,
        transform,
        backgroundColor: 'white',
        padding: '12px',
        borderRadius: 12,
        pointerEvents: 'auto',
        zIndex: 1000,
        minWidth: '323px',
        maxWidth: '400px',
        boxShadow:
          '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
      }}
    >
      <Stack
        display="flex"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={0.8}
      >
        <Typography variant="h6">
          {[address?.city, address?.stateAbbreviation]
            .filter(Boolean)
            .join(', ') || 'Unknown Location'}
        </Typography>

        <IconButton
          onClick={onClose}
          size={'small'}
          sx={{
            color: (theme) => theme.palette.action.active,
            cursor: 'pointer',
            borderRadius: '100px',
          }}
          data-testid="action-modal-close-btn"
          data-test-id="mapLibreAddLeadMapPinTooltipCloseBtn"
        >
          <CloseIcon fontSize={'small'} />
        </IconButton>
      </Stack>
      <Stack spacing={0.4}>
        {(address || label) && (
          <Typography
            variant="body2"
            sx={{ color: 'text.primary', fontSize: '14px' }}
          >
            {addressDisplay || label}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            color: 'rgb(14 40 63 / 0.7)',
          }}
        >
          {latitude.toFixed(6)}, {longitude.toFixed(6)}
        </Typography>

        {/* SH-1143 — Warning banner when record(s) here are hidden by the active filters */}
        {hiddenExists && (
          <MapBubbleContentsAlert message={HIDDEN_RECORDS_WARNING} />
        )}

        {!hiddenExists && knockStatusPreview.hasKnockStatusLocation && (
          <StatusSection
            knockStatus={applyClearedOverride(knockStatusPreview, cleared)}
            chipMarginTop={false}
            canEdit={canEdit}
            isSavingReason={isSavingReason}
            onSaveReason={handleSaveReason}
            clearButton={
              // SH-1781: cleared hides it before the ~3s tile-refetch lag.
              isD2dEnabled && hasKnockStatus && !cleared && canEdit ? (
                <ClearKnockStatusButton onClick={openClearConfirm} />
              ) : undefined
            }
            viewHistoryButton={
              // SH-1307 -- history is still viewable even with no active status
              // (e.g. it was deleted from Settings), so gate on real history
              // existing at all, not on there being a currently active one.
              // SH-1781: shows right after a clear; never hides earned history.
              (knockStatusPreview.hasHistory || cleared) && (
                <ViewKnockStatusHistoryButton
                  onClick={onViewKnockStatusHistory}
                />
              )
            }
          />
        )}

        <Box sx={{ pt: 1.5 }}>
          {hiddenExists ? (
            <UpdateFiltersButton onClick={handleUpdateFilters} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.8 }}>
              {isD2dEnabled && !showKnockStatusButton && (
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  sx={{ backgroundColor: '#ECF5FF', color: 'text.secondary' }}
                  onClick={() => onKnockStatusClick?.('', variant ?? '')}
                >
                  Knock Status
                </Button>
              )}
              <Button
                variant="contained"
                fullWidth
                onClick={onAddLead}
                sx={{ textTransform: 'none' }}
              >
                Add Lead
              </Button>
            </Box>
          )}
        </Box>
      </Stack>
      <Box
        sx={{
          position: 'absolute',
          ...arrowAnchorStyle(position, arrowOffset),
          margin: 0,
          backgroundColor: 'white',
          width: '30px',
          height: '30px',
          transform: 'rotate(45deg)',
          zIndex: -1,
        }}
      />

      <ClearKnockStatusConfirmDialog
        open={clearConfirmOpen}
        isClearing={isClearing}
        onCancel={closeClearConfirm}
        onConfirm={handleClearKnockStatus}
      />
    </div>
  )
}
